import {
    Divider,
    FormControlLabel,
    Grid,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import React, {useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    DeskDiscountGovernanceEntityResponse,
    DeskDiscountGovernanceUpdateRequest
} from "../../api/types/deskDiscountGovernance";
import {useDeskDiscountGovernanceRepository} from "../../api/repositories/DeskDiscountGovernanceRepository";
import {dateFormatter} from "../../utils";
import {formatDeskDiscountPercentage} from "./deskDiscountFormat";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {setSnackbar} from "../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../util/state/globalVariables";

/**
 * Form di modifica di un record di governance sconto al banco.
 *
 * Decisione TL: il record è immutabile tranne il flag `active` (toggle). Tutti gli altri campi
 * sono read-only. Per facilitare l'operatore, sotto al dettaglio è mostrata la lista di tutte le
 * configurazioni dello stesso scope (location + profilo + dealer + legal entity), attive e non,
 * ciascuna con il proprio toggle: al più una può essere attiva per periodi in overlap.
 *
 * @param row       record selezionato
 * @param onChange  callback invocata dopo un toggle andato a buon fine (refresh griglia + snackbar)
 */
const DeskDiscountGovernanceEditForm = ({
                                            row,
                                            onChange
                                        }: {
    row: DeskDiscountGovernanceEntityResponse;
    onChange: () => void;
}) => {

    const {t} = useTranslation(["translation", "pricing"]);

    const {getDeskDiscountGovernanceByScope, putDeskDiscountGovernance} = useDeskDiscountGovernanceRepository();
    const {dispatchGlobalData} = useContext(GlobalContext);

    const [scopeList, setScopeList] = useState<DeskDiscountGovernanceEntityResponse[]>([]);
    const [busyId, setBusyId] = useState<number | null>(null);

    /*
     * F25 (LPP-21950) - T2.60b, punto 6: la resa delle percentuali passa dal formattatore localizzato.
     * L'helper faceva `(v / 100).toFixed(2) + "%"`, quindi mostrava il punto decimale anche in
     * italiano (`2.50%`), sia sul dettaglio sia nella lista delle configurazioni dello stesso scope.
     * Resta l'alias `pct` perche' e' usato in quattro punti del render.
     */
    const pct = formatDeskDiscountPercentage;

    const fetchScopeList = async () => {
        try {
            const resp = await getDeskDiscountGovernanceByScope(row);
            const list = resp?.content ?? [];
            setScopeList([...list].sort((a, b) =>
                (a.validityDateFrom || "") > (b.validityDateFrom || "") ? 1 : -1));
        } catch (e) {
            // errore gestito da useApi
        }
    };

    useEffect(() => {
        fetchScopeList();
        // eslint-disable-next-line
    }, [row.id]);

    const handleToggle = async (item: DeskDiscountGovernanceEntityResponse, next: boolean) => {
        setBusyId(item.id);
        const request: DeskDiscountGovernanceUpdateRequest = {
            id: item.id,
            prcLocation: item.prcLocation,
            idLppProfile: item.idLppProfile,
            discountMin: item.discountMin,
            discountMax: item.discountMax,
            active: next,
            validityDateFrom: item.validityDateFrom,
            validityDateTo: item.validityDateTo,
            dealer: item.dealer,
            legalEntity: item.legalEntity,
        };
        try {
            await putDeskDiscountGovernance(request);
            await fetchScopeList();
            onChange();
        } catch (e: any) {
            if (e?.response?.status === 409) {
                // Estrae l'id in conflitto dal messaggio BE (pattern "conflicting id 'NNN'")
                const beMessage = e?.response?.data?.reportedErrors?.[0] ?? e?.response?.data?.message ?? "";
                const conflictMatch = beMessage.match(/conflicting id '(\d+)'/);
                const conflictId = conflictMatch?.[1] ?? "?";
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t("pricing:deskDiscountGovernance.errors.activationConflict", {conflictId}),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true
                        }
                    }
                });
            }
            // altri errori gestiti da useApi
        }
        setBusyId(null);
    };

    const readOnlyField = (label: string, value: string) => (
        <TextField label={label} value={value} size="small" fullWidth InputProps={{readOnly: true}} disabled/>
    );

    return (
        <Grid container spacing={2} alignItems="start">
            {/* DETTAGLIO RECORD SELEZIONATO (read-only) */}
            <Grid container item xs={12} spacing={2} rowSpacing={3}>
                <Grid item xs={12} md={3}>
                    {readOnlyField(t("pricing:deskDiscountGovernance.id"), String(row.id))}
                </Grid>
                <Grid item xs={12} md={3}>
                    {readOnlyField(t("pricing:dealer.tenant"), t("translation:legalEntities.".concat(row.legalEntity)))}
                </Grid>
                <Grid item xs={12} md={3}>
                    {readOnlyField(t("pricing:dealer.dealer"), t("translation:dealers.".concat(row.dealer)))}
                </Grid>
                <Grid item xs={12} md={3}>
                    {readOnlyField(t("pricing:deskDiscountGovernance.location"), row.prcLocation?.name || "")}
                </Grid>
                <Grid item xs={12} md={3}>
                    {readOnlyField(t("pricing:deskDiscountGovernance.profiles"), row.lppProfile?.name || "")}
                </Grid>
                <Grid item xs={12} md={1.5}>
                    {readOnlyField(t("pricing:deskDiscountGovernance.minPercentage"), pct(row.discountMin))}
                </Grid>
                <Grid item xs={12} md={1.5}>
                    {readOnlyField(t("pricing:deskDiscountGovernance.maxPercentage"), pct(row.discountMax))}
                </Grid>
                {/*
                  * F25 (LPP-21950) - T2.60b, punto 4: qui e nell'intestazione della tabella sotto si
                  * mostrano le date di validita' della configurazione, prima etichettate "Prenotazione
                  * dal/al" con la chiave condivisa `ancillary.form.labels`.
                  */}
                <Grid item xs={12} md={3}>
                    {readOnlyField(t("pricing:deskDiscountGovernance.validityFrom"), row.validityDateFrom ? dateFormatter(row.validityDateFrom) : "")}
                </Grid>
                <Grid item xs={12} md={3}>
                    {readOnlyField(t("pricing:deskDiscountGovernance.validityTo"), row.validityDateTo ? dateFormatter(row.validityDateTo) : "")}
                </Grid>
            </Grid>

            {/* LISTA CONFIGURAZIONI DELLO STESSO SCOPE + TOGGLE */}
            <Grid item xs={12}>
                <Divider sx={{my: 2}}/>
                <Typography variant="subtitle2" sx={{mb: 1}}>
                    {t("pricing:deskDiscountGovernance.scopeConfigsTitle")}
                </Typography>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t("pricing:deskDiscountGovernance.id")}</TableCell>
                            <TableCell>{t("pricing:deskDiscountGovernance.validityFrom")}</TableCell>
                            <TableCell>{t("pricing:deskDiscountGovernance.validityTo")}</TableCell>
                            <TableCell>{t("pricing:deskDiscountGovernance.minPercentage")}</TableCell>
                            <TableCell>{t("pricing:deskDiscountGovernance.maxPercentage")}</TableCell>
                            <TableCell>{t("pricing:deskDiscountGovernance.enabled")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {scopeList.map((item) => (
                            <TableRow key={item.id} selected={item.id === row.id}>
                                <TableCell>{item.id}</TableCell>
                                <TableCell>{item.validityDateFrom ? dateFormatter(item.validityDateFrom) : ""}</TableCell>
                                <TableCell>{item.validityDateTo ? dateFormatter(item.validityDateTo) : ""}</TableCell>
                                <TableCell>{pct(item.discountMin)}</TableCell>
                                <TableCell>{pct(item.discountMax)}</TableCell>
                                <TableCell>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={!!item.active}
                                                disabled={busyId != null}
                                                onChange={(e) => handleToggle(item, e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label={item.active
                                            ? t("pricing:deskDiscountGovernance.enabledYes")
                                            : t("pricing:deskDiscountGovernance.enabledNo")}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Grid>
        </Grid>
    );
};

export default DeskDiscountGovernanceEditForm;
